import analytics from '../../services/analytics';

export const INIT_VIDEO_STREAM_SUCCESS = 'INIT_VIDEO_STREAM_SUCCESS';
export const INIT_AUDIO_STREAM_SUCCESS = 'INIT_AUDIO_STREAM_SUCCESS';
export const INIT_SCREEN_STREAM_SUCCESS = 'INIT_SCREEN_STREAM_SUCCESS';
export const STOP_VIDEO_STREAM = 'STOP_VIDEO_STREAM';
export const STOP_AUDIO_STREAM = 'STOP_AUDIO_STREAM';
export const STOP_SCREEN_STREAM = 'STOP_SCREEN_STREAM';
export const SET_DEVICE_CONTROLLER = 'SET_DEVICE_CONTROLLER';
export const SET_MEETING_SESSION = 'SET_MEETING_SESSION';
export const STREAM_ERROR = 'STREAM_ERROR';
export const END_MEETING_SESSION = 'END_MEETING_SESSION';
export const INIT_DEVICES_SUCCESS = 'INIT_DEVICES_SUCCESS';
export const SET_SELECTED_DEVICE = 'SET_SELECTED_DEVICE';
export const OPEN_MEDIA_RECOVERY_MODAL = 'OPEN_MEDIA_RECOVERY_MODAL';
export const CLOSE_MEDIA_RECOVERY_MODAL = 'CLOSE_MEDIA_RECOVERY_MODAL';
export const SET_SCREEN_SHARE_INITIATED = 'SET_SCREEN_SHARE_INITIATED';
export const CLEAR_SCREEN_SHARE_INITIATED = 'CLEAR_SCREEN_SHARE_INITIATED';
export const VIDEO_TILE_DID_UPDATE = 'VIDEO_TILE_DID_UPDATE';
export const VIDEO_TILE_WAS_REMOVED = 'VIDEO_TILE_WAS_REMOVED';
export const SET_LOCAL_VIDEO_ELEMENT = 'SET_LOCAL_VIDEO_ELEMENT';
export const SET_LOCAL_MUTE = 'SET_LOCAL_MUTE';
export const SET_REMOTE_MUTE = 'SET_REMOTE_MUTE';
export const UPDATE_ATTENDEE = 'UPDATE_ATTENDEE';
export const REMOVE_ATTENDEE = 'REMOVE_ATTENDEE';
export const SET_WEBRTC_STREAM = 'SET_WEBRTC_STREAM';
export const SET_WEBRTC_SESSION_ID = 'SET_WEBRTC_SESSION_ID';

export const setWebrtcStream = stream => ({
  type: SET_WEBRTC_STREAM,
  payload: stream,
});

export const setWebrtcSessionId = sessionId => ({
  type: SET_WEBRTC_SESSION_ID,
  payload: sessionId,
});

export const initDevices = deviceController => async dispatch => {
  try {
    const videoInputs = await deviceController.listVideoInputDevices();
    const audioInputs = await deviceController.listAudioInputDevices();
    const audioOutputs = await deviceController.listAudioOutputDevices();

    dispatch({
      type: INIT_DEVICES_SUCCESS,
      payload: { videoInputs, audioInputs, audioOutputs },
    });

    dispatch({
      type: SET_DEVICE_CONTROLLER,
      payload: deviceController,
    });

    return deviceController;
  } catch (error) {
    dispatch({
      type: STREAM_ERROR,
      payload: 'Failed to initialize devices',
    });
    throw error;
  }
};

export const setSelectedDevice = (kind, deviceId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: SET_SELECTED_DEVICE,
      payload: { kind, deviceId },
    });
  } catch {
    dispatch({
      type: STREAM_ERROR,
      payload: `Failed to set ${kind} device`,
    });
  }
};

export const initVideoStream = () => async dispatch => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    analytics.trackCameraPermissionGranted();
    analytics.trackMicrophonePermissionGranted();
    dispatch({
      type: INIT_VIDEO_STREAM_SUCCESS,
      payload: stream,
    });
    return stream;
  } catch (error) {
    analytics.trackCameraPermissionDenied();
    const interviewId = localStorage.getItem('interview_id');
    if (error.name === 'NotAllowedError') {
      analytics.trackMediaPermissionDenied(interviewId, 'camera');
      analytics.trackMediaPermissionDenied(interviewId, 'microphone');
    } else {
      analytics.trackVideoFailureDetected(interviewId, error.message || 'unknown_error');
    }
    dispatch({
      type: STREAM_ERROR,
      payload: 'Failed to initialize video stream',
    });
    throw error;
  }
};

export const initAudioStream = () => async dispatch => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: true,
    });
    analytics.trackMicrophonePermissionGranted();
    dispatch({
      type: INIT_AUDIO_STREAM_SUCCESS,
      payload: stream,
    });
    return stream;
  } catch (error) {
    analytics.trackMicrophonePermissionDenied();
    const interviewId = localStorage.getItem('interview_id');
    if (error.name === 'NotAllowedError') {
      analytics.trackMediaPermissionDenied(interviewId, 'microphone');
    } else {
      analytics.trackAudioFailureDetected(interviewId, error.message || 'unknown_error');
    }
    dispatch({
      type: STREAM_ERROR,
      payload: 'Failed to initialize audio stream',
    });
    throw error;
  }
};

export const initScreenStream = () => async dispatch => {
  let screenShareEndedDispatched = false;

  const attemptScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' },
        audio: true,
      });

      analytics.trackScreenShareStarted();

      const handleScreenShareEnd = () => {
        if (!screenShareEndedDispatched) {
          screenShareEndedDispatched = true;
          analytics.trackScreenShareStopped();
          dispatch(stopScreenStream());
          dispatch(openMediaRecoveryModal('screenshare-stopped'));
        }
      };

      const [videoTrack] = stream.getVideoTracks();
      videoTrack.onended = handleScreenShareEnd;

      stream.getTracks().forEach(track => {
        track.onended = handleScreenShareEnd;
      });

      dispatch({
        type: INIT_SCREEN_STREAM_SUCCESS,
        payload: stream,
      });
      return stream;
    } catch (error) {
      analytics.trackScreenShareFailed(error.message || 'unknown_error');
      const interviewId = localStorage.getItem('interview_id');
      if (error.name === 'NotAllowedError') {
        analytics.trackMediaPermissionDenied(interviewId, 'screen_share');
      }
      dispatch(openMediaRecoveryModal('screenshare-stopped'));
      setTimeout(() => {
        attemptScreenShare();
      }, 1000);
      throw error;
    }
  };

  return attemptScreenShare();
};

export const stopVideoStream = () => (dispatch, getState) => {
  const { videoStream } = getState().assesmentRecording;
  if (videoStream) {
    videoStream.getTracks().forEach(track => track.stop());
  }
  dispatch({ type: STOP_VIDEO_STREAM });
};

export const stopAudioStream = () => (dispatch, getState) => {
  const { audioStream } = getState().assesmentRecording;
  if (audioStream) {
    audioStream.getTracks().forEach(track => track.stop());
  }
  dispatch({ type: STOP_AUDIO_STREAM });
};

export const stopScreenStream = () => (dispatch, getState) => {
  const { screenStream } = getState().assesmentRecording;
  if (screenStream) {
    screenStream.getTracks().forEach(track => track.stop());
  }
  dispatch({ type: STOP_SCREEN_STREAM });
};

export const setDeviceController = controller => ({
  type: SET_DEVICE_CONTROLLER,
  payload: controller,
});

export const endMeetingSession = meetingSession => dispatch => {
  if (meetingSession) {
    meetingSession.audioVideo.stopContentShare();
    meetingSession.audioVideo.stop();
  }
  dispatch({ type: END_MEETING_SESSION });
};

export const startMeetingSession = session => (dispatch, getState) => {
  const presenceObserver = {
    attendeesPresenceDidChange: (attendeeId, present, externalUserId, dropped) => {
      if (attendeeId.includes('#content')) return;
      if (present) {
        dispatch(
          updateAttendee(attendeeId, {
            hasLeft: false,
            isVideoOn: true,
            source: 'presence',
            dropped,
          }),
        );
      } else {
        dispatch(
          updateAttendee(attendeeId, {
            hasLeft: true,
            isVideoOn: false,
            source: 'presence',
            dropped,
          }),
        );
      }
    },
  };

  const videoTileObserver = {
    videoTileDidUpdate: tileState => {
      const { tileId, active, boundAttendeeId, isContent } = tileState;
      if (tileId === null || !boundAttendeeId) return;

      if (!active) {
        dispatch(
          updateAttendee(boundAttendeeId, { isVideoOn: false, tileId, tileState, source: 'tile' }),
        );
      } else if (active) {
        dispatch(
          updateAttendee(boundAttendeeId, { isVideoOn: true, tileId, tileState, source: 'tile' }),
        );
      }

      dispatch(videoTileDidUpdate(tileState));
    },
    videoTileWasRemoved: tileId => {
      const attendees = getState().interviewRecording.attendees;
      const attendee = Object.entries(attendees).find(([id, data]) => data.tileId === tileId);

      if (attendee) {
        const [attendeeId] = attendee;
        dispatch(updateAttendee(attendeeId, { isVideoOn: false, tileId: null }));
      }

      dispatch(videoTileWasRemoved(tileId));
    },
  };

  session.audioVideo.addObserver(videoTileObserver);
  session.audioVideo.realtimeSubscribeToAttendeeIdPresence(
    presenceObserver.attendeesPresenceDidChange,
  );

  session.audioVideo.start();
  session.audioVideo.startLocalVideoTile();

  dispatch({
    type: SET_MEETING_SESSION,
    payload: session,
  });
};

export const stopAllStreams = () => (dispatch, getState) => {
  const { videoStream, audioStream, screenStream } = getState().interviewRecording;

  const stopTracks = stream => {
    if (stream) {
      stream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (err) {
          console.warn('Failed to stop track:', err);
        }
      });
    }
  };

  stopTracks(videoStream);
  stopTracks(audioStream);
  stopTracks(screenStream);

  dispatch({ type: STOP_VIDEO_STREAM });
  dispatch({ type: STOP_AUDIO_STREAM });
  dispatch({ type: STOP_SCREEN_STREAM });

  console.log('All the streams stops or not');
};

export const setMeetingSession = session => ({
  type: SET_MEETING_SESSION,
  payload: session,
});

export const openMediaRecoveryModal = reason => ({
  type: OPEN_MEDIA_RECOVERY_MODAL,
  payload: reason || 'unknown',
});

export const closeMediaRecoveryModal = () => ({
  type: 'CLOSE_MEDIA_RECOVERY_MODAL',
});

export const setHasVideo = hasVideo => ({
  type: 'SET_HAS_VIDEO',
  payload: hasVideo,
});

export const setScreenShareInitiated = () => ({
  type: SET_SCREEN_SHARE_INITIATED,
  payload: Date.now(),
});

export const clearScreenShareInitiated = () => ({
  type: CLEAR_SCREEN_SHARE_INITIATED,
});

export const videoTileDidUpdate = tileState => ({
  type: VIDEO_TILE_DID_UPDATE,
  payload: { tileState },
});

export const videoTileWasRemoved = tileId => ({
  type: VIDEO_TILE_WAS_REMOVED,
  payload: { tileId },
});

export const setLocalVideoElement = element => ({
  type: SET_LOCAL_VIDEO_ELEMENT,
  payload: element,
});

export const setRemoteMute = (attendeeId, muted) => ({
  type: SET_REMOTE_MUTE,
  payload: { attendeeId, muted },
});

export const updateAttendee = (attendeeId, updates) => ({
  type: UPDATE_ATTENDEE,
  payload: { attendeeId, updates },
});

export const removeAttendee = attendeeId => ({
  type: REMOVE_ATTENDEE,
  payload: attendeeId,
});
